import Vue from "vue";
import deepmerge from "deepmerge";
import set from "lodash/set";
import get from "lodash/get";
import { _isMobile } from "../providers/utils/helpers";

const isProduction = process.env.NODE_ENV === "production";
const flujo = process.env.VUE_APP_CAMP || "ofertaDigital";

const _normalizeCharacters = (str) => {
  return str
    ? (str + "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
    : str;
};

const AdobeFunctions = {
  internal(context, _callback, clearVars = true) {
    if (clearVars) {
      AdobeFunctions.clearVars();
    }
    AdobeFunctions.setConstants();
    AdobeFunctions.setOptimizations(context);
    setTimeout(_callback, 0);
  },
  callAdobeDTM(method, event, setApplicationProps = true) {
    /**
     * Realizar llamada al DTM
     */
    if (event !== "Page View" && setApplicationProps) {
      // No se debe reportar los props de application para page views
      AdobeFunctions.setApplicationProps();
    }
    try {
      window[method](event);
      // Resetear flag de pageview automático
      window._pageViewAutomatico = false;
    } catch (error) {
      Vue.$log.error("🚀 Adobe Launch error: ", error);
    }
    Vue.$log.info("🚀 Adobe Launch call: ", event);
  },
  clearVars() {
    /**
     * Limpiar las variables cuando hace falta
     */
    set(window.digitalData, "products.attributes", []);
    set(window.digitalData, "internalCampaign.attributes", []);
    set(window.digitalData, "application.state", "");
    set(window.digitalData, "application.step", "");
    set(window.digitalData, "application.isQualifiedVisits", "");
    set(window.digitalData, "application.errorType", "");
    set(
      window.digitalData,
      "application.globalApplication",
      "onboarding fixed term deposit"
    );
    set(window.digitalData, "page.pageInfo.errorPage", "");
    set(window.digitalData, "page.pageInfo.level2", "");
    set(window.digitalData, "page.pageInfo.pageName", "");
    set(window.digitalData, "application.typology", "");
    set(window.digitalData, "page.pageInfo.pageIntent", "transaccion");
    set(window.digitalData, "user.segment.profile", "");
    set(
      window.digitalData,
      "application",
      deepmerge(window.digitalData.application, {
        application: {
          type: "",
          name: "",
        },
        fulfillmentModel: "",
      })
    );
  },
  setPageInstanceId() {
    /**
     * Establecer entorno en datalayer
     */
    set(window.digitalData, "pageInstanceID", isProduction ? "pro" : "de");
  },
  setIsMobile() {
    if (_isMobile()) {
      set(window.digitalData, "user.device.mobile", "si");
    } else {
      set(window.digitalData, "user.device.mobile", "no");
    }
  },
  setSysEnv() {
    if (_isMobile()) {
      set(window.digitalData, "page.pageInfo.sysEnv", "web-movil");
    } else {
      set(window.digitalData, "page.pageInfo.sysEnv", "escritorio");
    }
  },
  setPageName(pageName) {
    let segments = [
      get(window.digitalData, "page.pageInfo.sysEnv", "escritorio"),
      get(window.digitalData, "page.pageInfo.area", "publica"),
      get(window.digitalData, "page.pageInfo.pageSegment", "general"),
      get(window.digitalData, "page.pageInfo.level1", "plazo fijo open market"),
    ];
    if (pageName) {
      segments.push(pageName);
      set(window.digitalData, "page.pageInfo.level2", pageName);
    }
    set(window.digitalData, "page.pageInfo.pageName", segments.join(":"));
  },
  setConstants() {
    set(window.digitalData, "page.pageInfo.server", window.location.hostname);
    set(window.digitalData, "user.device.userAgent", navigator.userAgent);
    set(window.digitalData, "page.pageInfo.pageIntent", "transaccion");
    AdobeFunctions.setPageInstanceId();
    AdobeFunctions.setIsMobile();
    AdobeFunctions.setSysEnv();
  },
  setOptimizations(context) {
    if (context && context.state && context.state.adobeTargetExperience) {
      /**
       * Se especificó una experiencia de Target. Indicarla al tagging.
       * La experiencia DEBE venir de Target con el formato: `IDACTIVIDAD_EXPERIENCIA`
       * Ej: "AR072019_B"
       */
      let datosTarget = context.state.adobeTargetExperience.split("_");
      if (datosTarget.length === 2) {
        let attributes = [
          {
            idOptimization: datosTarget[0],
            experience: datosTarget[1],
            place: "",
            type: "",
            executor: "Target",
            audience: "",
          },
        ];
        set(window.digitalData, "optimization.attributes", attributes);
      }
    }
  },
  setInternalCampaign() {
    let attributes = [
      {
        location: "",
        campaignFormat: "",
        collectiveCode: "",
        campaignName: "",
        product: "",
        productCode: "",
        quantity: "",
      },
    ];
    set(window.digitalData, "internalCampaign.attributes", attributes);
  },
  setProductAttributes(context) {
    // Obtenemos los atributos actuales o sino en blanco
    let attributes = get(window.digitalData, "product.attributes", []);
    // Generamos una base de producto con lo que podamos traer del state
    let producto = {
      productName: context.mutation.payload.productName
        ? context.mutation.payload.productName
        : "plazo fijo open market",
      productSubtype: "plazos fijos",
      primaryCategory: "inversiones",
      amount: get(context, "state.simulacion.importe", "").replace(/\D/g, ""),
      currency:
        get(context, "state.route.name", "") === "simulacion" ? "" : "ARS",
      quantity: "1",
      interestRate: {
        tin: "",
        tae: "",
        rate: get(context, "state.simulacion.tasa", "")
          .replace(",", ".")
          .replace("%", ""),
      },
    };

    // El model de todos los atributos
    let attributesModel = {
      productName: "",
      productSubtype: "",
      primaryCategory: "",
      productCode: "",
      quantity: "",
      amount: "",
      paymentAmount: "",
      numberOfPayments: "",
      paymentDate: "",
      paymentType: "",
      serviceCharge: "",
      currency: "",
      numberOfHolders: "",
      interestRate: {
        tin: "",
        tae: "",
        rate: "",
      },
      term: "",
      group: "",
    };

    if (attributes.length === 0) {
      // Si no tenemos atributos, pusheamos este nuevo
      attributes.push(deepmerge(attributesModel, { ...producto }));
    } else {
      // Ya tenemos, modificamos
      set(attributes[0], deepmerge(attributesModel, { ...producto }));
    }
    set(window.digitalData, "products.attributes", attributes);
  },
  setProductState(state) {
    get(window.digitalData, "products.attributes", []).forEach(
      (product, index) => {
        set(window.digitalData.products.attributes, `[${index}].state`, state);
      }
    );
  },
  setApplicationProps() {
    set(
      window.digitalData,
      "application",
      deepmerge(window.digitalData.application, {
        application: {
          type: "contratacion",
          name: `plazo fijo open market`,
        },
        fulfillmentModel: "offline",
      })
    );
    set(
      window.digitalData,
      "application.typology",
      flujo === "ofertaDigital" ? "por pauta" : "por seo"
    );
  },
};

const Adobe = {
  enable() {},
  wait() {
    return !!window.digitalLink && !!(document.readyState === "complete");
  },
  init() {
    AdobeFunctions.internal(undefined, () => {
      Vue.$log.info("🚀 Adobe Launch working");
    });
  },
  appStartedClick(context) {
    AdobeFunctions.internal(
      context,
      () => {
        AdobeFunctions.setProductAttributes(context);
        set(
          window.digitalData,
          "application.step",
          _normalizeCharacters(context.mutation.payload)
        );
        set(window.digitalData, "application.isQualifiedVisits", "");
        AdobeFunctions.callAdobeDTM("digitalLink", "App Started");
      },
      false
    );
  },
  appOnClickStart(context) {
    AdobeFunctions.internal(
      context,
      () => {
        AdobeFunctions.setProductAttributes(context);
        AdobeFunctions.setProductState("");
        if (context.mutation.payload.step) {
          set(
            window.digitalData,
            "application.step",
            _normalizeCharacters(context.mutation.payload.step)
          );
        }
        if (context.mutation.payload.name) {
          set(
            window.digitalData,
            "application.application.name",
            _normalizeCharacters(context.mutation.payload.name)
          );
        }
        if (context.mutation.payload.type) {
          set(
            window.digitalData,
            "application.application.type",
            _normalizeCharacters(context.mutation.payload.type)
          );
        }
        if (context.mutation.payload.pageName) {
          AdobeFunctions.setPageName(context.mutation.payload.pageName);
        }
        set(window.digitalData, "page.pageInfo.pageIntent", "informacion");
        AdobeFunctions.callAdobeDTM("digitalLink", "App On Click Start", false);
      },
      true
    );
  },
  landing(context) {
    AdobeFunctions.internal(context, () => {
      AdobeFunctions.clearVars();
      set(window.digitalData, "page.pageInfo.pageIntent", "landing page");
      set(window.digitalData, "application.isQualifiedVisits", "true");
      AdobeFunctions.setPageName("");

      if (!window._pageViewAutomatico) {
        // Solo disparar el pageview manual si no se ha disparado el automático
        AdobeFunctions.callAdobeDTM("digitalView", "Page View");
      }
    });
  },
  paso1(context) {
    AdobeFunctions.internal(context, () => {
      // Valores dinámicos del paso 1
      AdobeFunctions.setPageName("1 simula tu plazo fijo");
      AdobeFunctions.setProductAttributes(context);
      // Ponemos el producto en inicio
      AdobeFunctions.setProductState("");
      set(window.digitalData, "page.pageInfo.level1", "plazo fijo open market");
      set(window.digitalData, "page.pageInfo.level2", "1 simula tu plazo fijo");
      set(window.digitalData, "application.step", "1 simula tu plazo fijo");
      set(window.digitalData, "application.isQualifiedVisits", "true");
      AdobeFunctions.callAdobeDTM("digitalView", "App Page Visit");
    });
  },
  noCliente(context) {
    AdobeFunctions.internal(context, () => {
      // Valores dinámicos del paso 2 (consultaContables)
      AdobeFunctions.setPageName("2 pasos a seguir");
      set(window.digitalData, "page.pageInfo.level1", "plazo fijo open market");
      set(window.digitalData, "page.pageInfo.level2", "2 pasos a seguir");
      set(window.digitalData, "application.step", "2 pasos a seguir");
      AdobeFunctions.setProductAttributes(context);
      // A partir de acá el state en blanco
      AdobeFunctions.setProductState("");

      AdobeFunctions.callAdobeDTM("digitalView", "App Step 2");
    });
  },
  cliente(context) {
    AdobeFunctions.internal(context, () => {
      AdobeFunctions.clearVars();

      AdobeFunctions.setPageName("plazo fijo cliente");
      set(window.digitalData, "page.pageInfo.level1", "plazo fijo open market");
      set(window.digitalData, "page.pageInfo.level2", "plazo fijo cliente");
      set(window.digitalData, "application.globalApplication", "");
      if (!window._pageViewAutomatico) {
        // Solo disparar el pageview manual si no se ha disparado el automático
        AdobeFunctions.callAdobeDTM("digitalView", "Page View");
      }
    });
  },
  paso2(context) {
    AdobeFunctions.internal(context, () => {
      // Valores dinámicos del paso 2 (consultaContables)
      AdobeFunctions.setPageName("3 datos personales");
      set(window.digitalData, "page.pageInfo.level1", "plazo fijo open market");
      set(window.digitalData, "page.pageInfo.level2", "3 datos personales");
      set(window.digitalData, "application.step", "3 datos personales");
      AdobeFunctions.setProductAttributes(context);
      // A partir de acá el state en blanco
      AdobeFunctions.setProductState("");

      AdobeFunctions.callAdobeDTM("digitalView", "App Step 3");
    });
  },
  paso3(context) {
    AdobeFunctions.internal(context, () => {
      // Valores dinámicos del paso 3 (precalificacion)
      AdobeFunctions.setPageName("4 datos laborales");
      set(window.digitalData, "page.pageInfo.level1", "plazo fijo open market");
      set(window.digitalData, "page.pageInfo.level2", "4 datos laborales");
      set(window.digitalData, "application.step", "4 datos laborales");
      AdobeFunctions.setProductAttributes(context);
      AdobeFunctions.setProductState("");

      AdobeFunctions.callAdobeDTM("digitalView", "App Step 4");
    });
  },
  paso4(context) {
    AdobeFunctions.internal(context, () => {
      // Valores dinámicos del paso 4 (envio)
      AdobeFunctions.setPageName("5 actividad");
      set(window.digitalData, "page.pageInfo.level1", "plazo fijo open market");
      set(window.digitalData, "page.pageInfo.level2", "5 actividad");
      set(window.digitalData, "application.step", "5 actividad");
      AdobeFunctions.setProductAttributes(context);
      AdobeFunctions.setProductState("");

      AdobeFunctions.callAdobeDTM("digitalView", "App Step 5");
    });
  },
  paso5(context) {
    AdobeFunctions.internal(context, () => {
      // Valores dinámicos del paso 5 (envio)
      AdobeFunctions.setPageName("6 datos de la cuenta");
      set(window.digitalData, "page.pageInfo.level1", "plazo fijo open market");
      set(window.digitalData, "page.pageInfo.level2", "6 datos de la cuenta");
      set(window.digitalData, "application.step", "6 datos de la cuenta");
      AdobeFunctions.setProductAttributes(context);
      AdobeFunctions.setProductState("");

      AdobeFunctions.callAdobeDTM("digitalView", "App Step 6");
    });
  },
  paso6(context) {
    AdobeFunctions.internal(context, () => {
      // Valores dinámicos del paso 6
      AdobeFunctions.setPageName("7 confirmacion");
      set(window.digitalData, "page.pageInfo.level1", "plazo fijo open market");
      set(window.digitalData, "page.pageInfo.level2", "7 confirmacion");
      set(window.digitalData, "application.step", "7 confirmacion");
      AdobeFunctions.setProductAttributes(context);
      AdobeFunctions.setProductState("");

      AdobeFunctions.callAdobeDTM("digitalView", "App Step 7");
    });
  },
  paso7(context) {
    AdobeFunctions.internal(context, () => {
      // Valores dinámicos del paso 7 (envio)
      AdobeFunctions.setPageName("8 verificando");
      set(window.digitalData, "page.pageInfo.level1", "plazo fijo open market");
      set(window.digitalData, "page.pageInfo.level2", "8 verificando");
      set(window.digitalData, "application.step", "8 verificando");
      AdobeFunctions.setProductAttributes(context);
      AdobeFunctions.setProductState("");

      AdobeFunctions.callAdobeDTM("digitalView", "App Step 8");
    });
  },
  gracias(context) {
    AdobeFunctions.internal(context, () => {
      AdobeFunctions.setPageName("pagina exitosa");
      set(window.digitalData, "page.pageInfo.level1", "plazo fijo open market");
      set(window.digitalData, "page.pageInfo.level2", "pagina exitosa");
      set(window.digitalData, "application.step", "pagina exitosa");
      set(window.digitalData, "application.state", "aprobado");
      AdobeFunctions.setProductAttributes(context);
      AdobeFunctions.setProductState("aprobado");

      AdobeFunctions.callAdobeDTM("digitalView", "App Completed");
    });
  },
  mensaje(context) {
    AdobeFunctions.internal(context, () => {
      // Para los errores se disparan eventos diferentes
      // de acuerdo al error por lo que en cada case debe llamarse a adobeDTM
      // Huella avanzada: errores que puede volver
      // app completed: finaliza el proceso y no puede volver
      AdobeFunctions.clearVars();
      AdobeFunctions.setPageName("pagina exitosa");
      set(window.digitalData, "page.pageInfo.level1", "plazo fijo open market");
      set(window.digitalData, "page.pageInfo.level2", "pagina exitosa");
      set(window.digitalData, "application.step", "pagina exitosa");
      set(window.digitalData, "application.state", "rechazado");
      set(window.digitalData, "user.segment.profile", "error general");
      set(window.digitalData, "application.globalApplication", "");
      AdobeFunctions.setProductAttributes(context);
      AdobeFunctions.setProductState("rechazado");

      AdobeFunctions.callAdobeDTM("digitalView", "App Completed");
    });
  },
  errorInesperado(context) {
    AdobeFunctions.internal(context, () => {
      AdobeFunctions.clearVars();
      AdobeFunctions.setPageName("error general");
      set(window.digitalData, "page.pageInfo.level1", "plazo fijo open market");
      set(window.digitalData, "page.pageInfo.level2", "error general");
      set(window.digitalData, "page.pageInfo.errorPage", "error general");
      set(window.digitalData, "page.pageInfo.pageIntent", "informacion");
      set(window.digitalData, "application.globalApplication", "");

      AdobeFunctions.callAdobeDTM("digitalView", "Page View");
    });
  },
  mensajeInvalido(context) {
    AdobeFunctions.internal(context, () => {
      AdobeFunctions.clearVars();
      AdobeFunctions.setPageName("error datos de identidad");
      set(window.digitalData, "page.pageInfo.level1", "plazo fijo open market");
      set(
        window.digitalData,
        "page.pageInfo.level2",
        "error datos de identidad"
      );
      set(
        window.digitalData,
        "page.pageInfo.errorPage",
        "error datos de identidad"
      );
      set(window.digitalData, "page.pageInfo.pageIntent", "informacion");
      set(window.digitalData, "application.globalApplication", "");

      AdobeFunctions.callAdobeDTM("digitalView", "Page View");
    });
  },
  pepsosi(context) {
    AdobeFunctions.internal(context, () => {
      AdobeFunctions.clearVars();
      AdobeFunctions.setPageName("pagina exitosa");
      set(window.digitalData, "page.pageInfo.level1", "plazo fijo open market");
      set(window.digitalData, "page.pageInfo.level2", "pagina exitosa");
      set(window.digitalData, "application.step", "pagina exitosa");
      set(window.digitalData, "application.state", "rechazado");
      set(window.digitalData, "user.segment.profile", "lista negra");
      set(window.digitalData, "application.globalApplication", "");
      AdobeFunctions.setProductAttributes(context);
      AdobeFunctions.setProductState("rechazado");

      AdobeFunctions.callAdobeDTM("digitalView", "App Completed");
    });
  },
};

export default Adobe;
